// Décodage de code-barres réel — port fidèle de public/modules/barcode-decode.js
// (StoreFlowProV2 vanilla). La vraie app ne fait jamais de lecture vidéo en direct :
// elle capture UNE photo (input file capture="environment", ouvre l'appareil photo
// natif sur mobile) puis essaie chaque moteur de décodage disponible l'un après
// l'autre sur cette même photo — BarcodeDetector natif (absent sur iOS Safari, d'où
// les deux secours), ZBar (WASM, fonctionne partout), puis Html5Qrcode en dernier
// recours. cleanCode() doit toujours être appliqué au résultat avant de le comparer
// aux codes déjà stockés sur un article (mêmes règles que le vanilla).
const NATIVE_FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"];
const ZBAR_ALLOWED_TYPES = new Set([8, 9, 12, 13, 39, 128]);
const PHOTO_DECODE_MAX_SIDE = 1600;

export function cleanCode(value) {
  return String(value ?? "").trim().replace(/[^0-9A-Za-z]/g, "");
}

async function drawScaledBitmap(bitmap) {
  const scale = Math.min(1, PHOTO_DECODE_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d", { willReadFrequently: true }).drawImage(bitmap, 0, 0, w, h);
  return canvas;
}

// Chargement paresseux des bibliothèques de secours (ZBar WASM 333 Ko + Html5Qrcode
// 375 Ko) — jusqu'ici deux <script defer> globaux dans index.html, donc téléchargés/
// exécutés sur CHAQUE écran de l'app (Admin, Planning...), jamais juste Scanner/Kühlung.
// Retour utilisateur (2026-07-31) : un crash "page blanche" persistant en PWA installée,
// jamais reproduit dans un onglet Safari normal, absent de la toute première version de
// l'artefact (avant toute vraie donnée) — cohérent avec une PWA iOS tuée pour dépassement
// mémoire plutôt qu'une exception JS ponctuelle (plusieurs correctifs ciblés déjà tentés
// sans résultat, voir storeflow_pwa_stale_bundle_crash). Ne charge donc ces bibliothèques
// QUE quand une vraie photo est effectivement décodée, jamais au chargement de l'app.
let vendorLoadPromise = null;
function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Échec de chargement : ${src}`));
    document.body.appendChild(script);
  });
}
function ensureBarcodeVendorsLoaded() {
  if (!vendorLoadPromise) {
    vendorLoadPromise = Promise.all([
      loadScriptOnce("/vendor/zbar-wasm.min.js").catch(() => {}),
      loadScriptOnce("/vendor/html5-qrcode.min.js").catch(() => {}),
    ]);
  }
  return vendorLoadPromise;
}

let filePhotoScanner = null;
function getFilePhotoScanner() {
  if (!window.Html5Qrcode) return null;
  if (!document.getElementById("nxBarcodeDecodeSink")) {
    const sink = document.createElement("div");
    sink.id = "nxBarcodeDecodeSink";
    sink.style.display = "none";
    document.body.appendChild(sink);
  }
  filePhotoScanner = filePhotoScanner || new window.Html5Qrcode("nxBarcodeDecodeSink", { verbose: false });
  return filePhotoScanner;
}

// Essaie chaque moteur l'un après l'autre sur la même photo statique, remonte un
// diagnostic précis de ce que chacun a répondu (attempts) — jamais un simple "rien
// trouvé" sans savoir pourquoi.
export async function decodeBarcodePhoto(file) {
  await ensureBarcodeVendorsLoaded();
  const attempts = [];
  let bitmap;

  try {
    bitmap = await createImageBitmap(file);
  } catch (err) {
    return { value: "", attempts: [`lecture image impossible (${err.message || err}) — format non supporté ?`] };
  }

  let canvas;
  try {
    canvas = await drawScaledBitmap(bitmap);
  } catch (err) {
    bitmap.close();
    return { value: "", attempts: [`redimensionnement impossible (${err.message || err})`] };
  }
  bitmap.close();

  if ("BarcodeDetector" in window) {
    try {
      const supported = await window.BarcodeDetector.getSupportedFormats().catch(() => []);
      const formats = NATIVE_FORMATS.filter((format) => supported.includes(format));
      if (formats.length) {
        const detector = new window.BarcodeDetector({ formats });
        const results = await detector.detect(canvas);
        if (results?.length && results[0].rawValue) {
          return { value: results[0].rawValue, attempts: [...attempts, "trouvé via natif"] };
        }
        attempts.push("natif : aucun code trouvé");
      } else {
        attempts.push("natif : pas de format code-barres supporté");
      }
    } catch (err) {
      attempts.push(`natif : erreur (${err.message || err})`);
    }
  } else {
    attempts.push("natif : indisponible sur ce navigateur");
  }

  if (window.zbarWasm && typeof WebAssembly !== "undefined") {
    try {
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const scanner = await window.zbarWasm.getDefaultScanner();
      const symbols = await window.zbarWasm.scanImageData(imageData, scanner);
      if (symbols?.length) {
        const hit = symbols.find((s) => ZBAR_ALLOWED_TYPES.has(s.type)) || symbols[0];
        const value = hit.decode();
        if (value) return { value, attempts: [...attempts, "trouvé via ZBar"] };
      }
      attempts.push(`ZBar : aucun code trouvé (${symbols?.length || 0} symbole(s) vu(s))`);
    } catch (err) {
      attempts.push(`ZBar : erreur (${err.message || err})`);
    }
  } else {
    attempts.push("ZBar : indisponible sur ce navigateur");
  }

  if (window.Html5Qrcode) {
    try {
      const scanner = getFilePhotoScanner();
      const scaledBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
      const scaledFile = new File([scaledBlob], "scan.jpg", { type: "image/jpeg" });
      const decodedText = await scanner.scanFile(scaledFile, false);
      if (decodedText) return { value: decodedText, attempts: [...attempts, "trouvé via Standard"] };
    } catch (err) {
      attempts.push(`Standard : aucun code trouvé (${err.message || err})`);
    }
  } else {
    attempts.push("Standard : bibliothèque non chargée");
  }

  return { value: "", attempts };
}
